import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { AnimatedViews, Container, useResponsive } from '@garnish/core';

import { GiftCardsGrid } from '../GiftCardsGrid';
import { GiftCardsHeader } from '../GiftCardsHeader';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsLoading = () => {
  const { currentBreakpoint, match } = useResponsive();
  const { right: safeAreaRightInset } = useSafeAreaInsets();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyRightInset = currentBreakpoint.isXS && safeAreaRightInset > 0;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const headingStyles = [styles.heading, match([styles.headingXS, styles.headingSM])];
  const subheadingStyles = [styles.subheading, match([styles.subheadingXS, styles.subheadingSM])];
  const chipsContainerDynamicStyles = useStyle(
    () => ({ marginRight: shouldApplyRightInset ? safeAreaRightInset : 0 }),
    [shouldApplyRightInset, safeAreaRightInset],
  );
  const chipsContainerStyles = [styles.chipsContainer, chipsContainerDynamicStyles];
  const chipsStyles = [styles.chips, match([styles.chipsXS, styles.chipsSM])];
  const chipStyle = match([styles.chipXS, styles.chipSM]);
  const shortChipStyles = [styles.chip, chipStyle, styles.chipShort];
  const mediumChipStyles = [styles.chip, chipStyle, styles.chipMedium];
  const longChipStyles = [styles.chip, chipStyle, styles.chipLong];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <GiftCardsHeader.Container>
        <AnimatedViews.Pulse style={headingStyles} />
        <AnimatedViews.Pulse style={subheadingStyles} />
      </GiftCardsHeader.Container>

      <View style={chipsContainerStyles}>
        <Container style={chipsStyles}>
          <AnimatedViews.Pulse index={1} style={mediumChipStyles} />
          <AnimatedViews.Pulse index={1} style={longChipStyles} />
          <AnimatedViews.Pulse index={1} style={shortChipStyles} />
          <AnimatedViews.Pulse index={1} style={longChipStyles} />
        </Container>
      </View>

      <GiftCardsGrid.Container>
        {CARD_PLACEHOLDER_INDEXES.map((index) => (
          <AnimatedViews.Pulse key={index} index={index + 2} style={styles.card} />
        ))}
      </GiftCardsGrid.Container>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_PLACEHOLDER_INDEXES = [...Array(6).keys()];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  heading: {
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  headingXS: {
    width: '64%',
    height: 28,
    marginVertical: 4,
  },
  headingSM: {
    width: 360,
    height: 44,
    marginVertical: 6,
    alignSelf: 'center',
  },
  subheading: {
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  subheadingXS: {
    width: '52%',
    height: 12,
    marginVertical: 3,
  },
  subheadingSM: {
    width: 240,
    height: 16,
    marginVertical: 4,
    alignSelf: 'center',
  },
  chipsContainer: {
    overflow: 'hidden',
  },
  chips: {
    flexDirection: 'row',
    paddingVertical: theme.spacing['4'],
  },
  chipsXS: {
    columnGap: theme.spacing['2'],
  },
  chipsSM: {
    justifyContent: 'center',
    columnGap: theme.spacing['4'],
  },
  chip: {
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  chipXS: {
    height: 32,
  },
  chipSM: {
    height: 40,
  },
  chipShort: {
    width: 64,
  },
  chipMedium: {
    width: 80,
  },
  chipLong: {
    width: 104,
  },
  card: {
    aspectRatio: '4 / 3',
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
});
